import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { knowledgeService, type KnowledgeArticle } from '@/services/knowledgeService';

const statusVariants: Record<string, 'success' | 'secondary'> = {
  published: 'success',
  draft: 'secondary',
};

export function KnowledgeBase() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(20);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    knowledgeService.getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    loadArticles();
  }, [currentPage, currentPageSize, filters]);

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      const result = await knowledgeService.listArticles({
        category: filters.category || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
        page: currentPage,
        pageSize: currentPageSize,
      });
      setArticles(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const columns = [
    {
      key: 'title',
      title: 'Title',
      render: (value: string, record: KnowledgeArticle) => (
        <span className="text-foreground font-medium">{value}</span>
      ),
    },
    {
      key: 'category',
      title: 'Category',
      render: (value: string | null) => value ? (
        <Badge variant="outline">{value}</Badge>
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value: string) => (
        <Badge variant={statusVariants[value] || 'secondary'}>{value}</Badge>
      ),
    },
    {
      key: 'updated_at',
      title: 'Updated',
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, record: KnowledgeArticle) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/knowledge/${record.id}`)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
      ],
    },
    ...(categories.length > 0
      ? [{
          key: 'category',
          label: 'Category',
          options: categories.map((cat) => ({ label: cat, value: cat })),
        }]
      : []),
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Knowledge Base"
        description="Manage articles for the AI-powered help system."
      >
        <Button variant="primary" size="sm" onClick={() => navigate('/admin/knowledge/new')}>
          New Article
        </Button>
      </PageHeader>

      <Table
        data={articles}
        columns={columns}
        loading={isLoading}
        searchable={true}
        filters={filterOptions}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
        emptyText="No articles yet. Create your first knowledge base article to power the AI help system."
        serverSide={true}
        onFilter={handleFilter}
        onPaginate={handlePaginate}
        totalItems={total}
      />
    </PageContainer>
  );
}
